// src/components/BCP/ChapterChecklist.tsx - Chapter completion checklist for BCP generation
import React from 'react';
import { FormState } from '../../types/bcpForm';

interface ChapterChecklistProps {
  formData: FormState;
  currentStep: number;
  onNavigateToStep?: (stepNumber: number) => void;
}

const ChapterChecklist: React.FC<ChapterChecklistProps> = ({ formData, currentStep, onNavigateToStep }) => {
  // Define BCP chapters with their completion criteria
  const chapters = [
    {
      id: 'business-info',
      title: 'Business Information',
      description: 'Company details and primary location',
      stepNumber: 1,
      isCompleted: () => {
        return !!(
          formData.business_name &&
          formData.business_type &&
          formData.industry &&
          formData.employee_count &&
          formData.primary_location.name &&
          formData.primary_location.address
        );
      }
    },
    {
      id: 'risk-assessment',
      title: 'Risk Assessment',
      description: 'Threat identification and analysis',
      stepNumber: 2,
      isCompleted: () => {
        // This would be completed if risk assessment step was filled
        return currentStep >= 2;
      }
    },
    {
      id: 'business-impact',
      title: 'Business Impact Analysis',
      description: 'Financial and operational impact assessment',
      stepNumber: 3,
      isCompleted: () => {
        return currentStep >= 3;
      }
    },
    {
      id: 'critical-functions',
      title: 'Critical Functions',
      description: 'Business-critical processes and RTO/RPO',
      stepNumber: 4,
      isCompleted: () => {
        return formData.critical_functions && formData.critical_functions.length > 0;
      }
    },
    {
      id: 'suppliers',
      title: 'Key Suppliers',
      description: 'Critical vendor dependencies',
      stepNumber: 4,
      isCompleted: () => {
        return formData.key_suppliers && formData.key_suppliers.length > 0;
      }
    },
    {
      id: 'it-systems',
      title: 'IT Systems',
      description: 'Technology infrastructure and dependencies',
      stepNumber: 4,
      isCompleted: () => {
        return formData.it_systems && formData.it_systems.length > 0;
      }
    },
    {
      id: 'locations',
      title: 'Additional Locations',
      description: 'Secondary and backup locations',
      stepNumber: 1,
      isCompleted: () => {
        // Chapter is completed if:
        // 1. User has additional locations, OR
        // 2. User has only one location (primary location exists and no additional locations)
        const hasAdditionalLocations = formData.additional_locations && formData.additional_locations.length > 0;
        const hasOnlyPrimaryLocation = formData.primary_location &&
          formData.primary_location.name &&
          formData.primary_location.address &&
          (!formData.additional_locations || formData.additional_locations.length === 0);

        return hasAdditionalLocations || hasOnlyPrimaryLocation;
      }
    },
    {
      id: 'team-contacts',
      title: 'Team & Contacts',
      description: 'Team members and service providers',
      stepNumber: 4,
      isCompleted: () => {
        return currentStep >= 4;
      }
    },
    {
      id: 'mitigations',
      title: 'Risk Mitigations',
      description: 'Strategies to mitigate identified risks',
      stepNumber: 5,
      isCompleted: () => {
        return currentStep >= 5;
      }
    },
    {
      id: 'review',
      title: 'Review & Finalize',
      description: 'Final review and BCP generation',
      stepNumber: 6,
      isCompleted: () => {
        return currentStep >= 6;
      }
    }
  ];

  const completedChapters = chapters.filter(chapter => chapter.isCompleted()).length;
  const totalChapters = chapters.length;
  const completionPercentage = Math.round((completedChapters / totalChapters) * 100);

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">BCP Chapters</h3>
        <p className="text-sm text-gray-600">
          {completedChapters} of {totalChapters} chapters completed ({completionPercentage}%)
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Chapter List */}
      <div className="space-y-3">
        {chapters.map((chapter) => {
          const isCompleted = chapter.isCompleted();
          const isCurrentStep = chapter.stepNumber === currentStep;
          return (
            <button
              key={chapter.id}
              onClick={() => onNavigateToStep?.(chapter.stepNumber)}
              className={`w-full text-left flex items-start p-3 rounded-lg border transition-all duration-200 hover:shadow-sm ${
                isCurrentStep
                  ? 'bg-blue-50 border-blue-200 ring-2 ring-blue-200'
                  : isCompleted
                  ? 'bg-green-50 border-green-200 hover:bg-green-100'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <div className="flex-shrink-0 mr-3 mt-0.5">
                {isCompleted ? (
                  <div className="flex justify-center items-center w-5 h-5 bg-green-500 rounded-full">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ) : isCurrentStep ? (
                  <div className="flex justify-center items-center w-5 h-5 bg-blue-500 rounded-full">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full bg-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${
                  isCurrentStep
                    ? 'text-blue-900'
                    : isCompleted
                    ? 'text-green-900'
                    : 'text-gray-900'
                }`}>
                  {chapter.title}
                  {isCurrentStep && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )}
                </div>
                <div className={`text-xs ${
                  isCurrentStep
                    ? 'text-blue-700'
                    : isCompleted
                    ? 'text-green-700'
                    : 'text-gray-500'
                }`}>
                  {chapter.description}
                </div>
              </div>
              <div className="flex-shrink-0 ml-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>

      {/* Completion Summary */}
      {completionPercentage === 100 && (
        <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-green-800">
                All chapters completed! Ready to generate your BCP.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterChecklist;
